import type { Tournament } from '@/lib/types/auction';
import type { ValidationItem } from './StatusChangeDialog';

export interface ValidationResult {
  isValid: boolean;
  validations: ValidationItem[];
}

export function validateOpenRegistration(
  tournament: Tournament
): ValidationResult {
  const validations: ValidationItem[] = [
    {
      label: 'Base Budget',
      status: tournament.baseBudget > 0 ? 'success' : 'error',
      message: tournament.baseBudget > 0 ? 'Configured' : 'Not configured',
    },
    {
      label: 'Team Limits',
      status:
        tournament.totalTeams > 0 && tournament.maxPlayersPerTeam > 0
          ? 'success'
          : 'error',
      message:
        tournament.totalTeams > 0 && tournament.maxPlayersPerTeam > 0
          ? `${tournament.totalTeams} teams, ${tournament.maxPlayersPerTeam} players each`
          : 'Team limits not set',
    },
    {
      label: 'Teams',
      status:
        tournament.teams?.length === tournament.totalTeams
          ? 'success'
          : 'error',
      message:
        tournament.teams?.length === tournament.totalTeams
          ? `All ${tournament.totalTeams} teams configured`
          : tournament.teams?.length === 0
            ? 'No teams configured yet'
            : `Only ${tournament.teams.length}/${tournament.totalTeams} teams configured. All teams must be added before opening registration.`,
    },
    {
      label: 'Registration Deadline',
      status: tournament.registrationDeadline ? 'success' : 'error',
      message: tournament.registrationDeadline
        ? new Date(tournament.registrationDeadline).toLocaleDateString()
        : 'Not set',
    },
    {
      label: 'Pools Configuration',
      status: tournament.pools?.length > 0 ? 'success' : 'error',
      message:
        tournament.pools?.length > 0
          ? `${tournament.pools.length} pool(s) configured`
          : 'At least one pool must be created',
    },
  ];

  return {
    isValid: validations.every((v) => v.status !== 'error'),
    validations,
  };
}

export function validateCloseRegistration(
  tournament: Tournament
): ValidationResult {
  const averagePlayersPerTeam = Math.floor(
    tournament.totalEnrollments / tournament.totalTeams
  );

  const now = new Date();
  const registrationDeadline = new Date(tournament.registrationDeadline);

  const validations: ValidationItem[] = [
    {
      label: 'Teams Registered',
      status:
        tournament.teams.length === tournament.totalTeams ? 'success' : 'error',
      message:
        tournament.teams.length === tournament.totalTeams
          ? `All ${tournament.totalTeams} teams registered`
          : tournament.teams.length === 0
            ? 'No teams registered'
            : `Only ${tournament.teams.length} team(s) registered. Exactly ${tournament.totalTeams} teams are required.`,
    },
    {
      label: 'Players in Pools',
      status:
        tournament.totalEnrollments > 0
          ? tournament.totalEnrollments >=
            tournament.totalTeams * tournament.maxPlayersPerTeam
            ? 'success'
            : 'warning'
          : 'error',
      message:
        tournament.totalEnrollments > 0
          ? tournament.totalEnrollments <
            tournament.totalTeams * tournament.maxPlayersPerTeam
            ? `Only ${averagePlayersPerTeam} player(s) available per team on average`
            : `${tournament.totalEnrollments} player(s) enrolled`
          : 'No players assigned to pools',
    },
    {
      label: 'Registration Period',
      status: now < registrationDeadline ? 'warning' : 'success',
      message:
        now < registrationDeadline
          ? `Registration deadline (${registrationDeadline.toLocaleDateString()}) has not been reached yet`
          : 'Registration period complete',
    },
  ];

  return {
    isValid: validations.every((v) => v.status !== 'error'),
    validations,
  };
}

export function validateStartAuction(tournament: Tournament): ValidationResult {
  const now = new Date();
  const auctionDate = new Date(tournament.auctionDate);
  const averagePlayersPerTeam = Math.floor(
    tournament.totalEnrollments / tournament.totalTeams
  );

  const validations: ValidationItem[] = [
    {
      label: 'Auction Date',
      status: now >= auctionDate ? 'success' : 'warning',
      message:
        now >= auctionDate
          ? 'Auction date has arrived'
          : `Auction is scheduled for ${auctionDate.toLocaleDateString()}. Starting early may affect participation.`,
    },
    {
      label: 'Teams Ready',
      status:
        tournament.teams.length >= tournament.totalTeams ? 'success' : 'error',
      message:
        tournament.teams.length >= tournament.totalTeams
          ? `${tournament.teams.length} team(s) ready`
          : `Need ${tournament.totalTeams} teams, only ${tournament.teams.length} registered`,
    },
    {
      label: 'Pool Players',
      status:
        tournament.totalEnrollments > 0
          ? tournament.totalEnrollments >= tournament.totalTeams
            ? 'success'
            : 'warning'
          : 'error',
      message:
        tournament.totalEnrollments > 0
          ? tournament.totalEnrollments <
            tournament.totalTeams * tournament.maxPlayersPerTeam
            ? `Only ${averagePlayersPerTeam} players available per team on average`
            : 'Sufficient players available for auction'
          : 'No players in pools',
    },
    {
      label: 'Team Budgets',
      status: tournament.teams.every((t) => t.budget > 0) ? 'success' : 'error',
      message: tournament.teams.every((t) => t.budget > 0)
        ? 'All teams have budget'
        : 'Some teams have no budget',
    },
    {
      label: 'Pool Configuration',
      status: tournament.pools.length > 0 ? 'success' : 'error',
      message:
        tournament.pools.length > 0
          ? `${tournament.pools.length} pool(s) configured`
          : 'At least one pool must be created',
    },
  ];

  return {
    isValid: validations.every((v) => v.status !== 'error'),
    validations,
  };
}

export function validateEndAuction(tournament: Tournament): ValidationResult {
  const now = new Date();
  const startDate = new Date(tournament.startDate);
  const totalPlayers = tournament.totalTeams * tournament.maxPlayersPerTeam;
  const playersBought = tournament.teams.reduce(
    (acc, team) => acc + team.playerCount,
    0
  );

  const validations: ValidationItem[] = [
    {
      label: 'Auction Progress',
      status:
        playersBought === totalPlayers
          ? 'success'
          : playersBought > 0
            ? 'warning'
            : 'error',
      message: `${playersBought}/${totalPlayers} player(s) bought`,
    },
    {
      label: 'Team Rosters',
      status: tournament.teams.every((t) => t.playerCount > 0)
        ? 'success'
        : 'warning',
      message: tournament.teams.every((t) => t.playerCount > 0)
        ? 'All teams have players'
        : 'Some teams have no players',
    },
    {
      label: 'Tournament Duration',
      status: now >= startDate ? 'success' : 'warning',
      message:
        now >= startDate
          ? 'Tournament period complete'
          : `Tournament is scheduled to start on ${startDate.toLocaleDateString()}.`,
    },
  ];

  return {
    isValid: validations.every((v) => v.status !== 'error'),
    validations,
  };
}

export const getStatusChangeContent = (
  status: Tournament['status']
): {
  title: string;
  description: string;
  impacts: string[];
  warningLevel: 'info' | 'warning' | 'danger';
} => {
  switch (status) {
    case 'DRAFT':
      return {
        title: 'Open Tournament Registration',
        description:
          'This will open the tournament for team registration. Some settings will be locked after this action.',
        impacts: [
          'Tournament owner will no longer be able to add or configure teams',
          'Core tournament settings will be locked',
          'Tournament details will be public',
          'Teams can start registering players',
        ],
        warningLevel: 'warning',
      };

    case 'REGISTRATION':
      return {
        title: 'Close Tournament Registration',
        description:
          'This will close team registration and prepare the tournament for auction.',
        impacts: [
          'Team registration will be disabled',
          'Pool assignments will be locked',
          'Tournament will move to upcoming state',
          'Teams can start preparing for auction',
        ],
        warningLevel: 'warning',
      };

    case 'UPCOMING':
      return {
        title: 'Start Tournament Auction',
        description:
          'This will start the live auction process. Make sure all teams are ready.',
        impacts: [
          'Live auction will begin',
          'Team budgets will be active',
          'Real-time bidding will be enabled',
          'Team owners can start bidding',
        ],
        warningLevel: 'warning',
      };

    case 'ACTIVE':
      return {
        title: 'End Tournament Auction',
        description: 'This will end the auction and finalize all team rosters.',
        impacts: [
          'All auctions will be closed',
          'Team rosters will be finalized',
          'Results will be locked',
          'No further changes will be allowed',
        ],
        warningLevel: 'danger',
      };

    default:
      return {
        title: 'Change Tournament Status',
        description: 'This will change the tournament status.',
        impacts: ['Tournament status will be updated'],
        warningLevel: 'warning',
      };
  }
};
